import { DestroyRef, Injectable, InjectionToken, inject } from '@angular/core';
import { SupabaseService } from '../../../shared/services/supabase.service';
import {
  type ChunkProgress,
  type ChunkProgressReporter,
  reportChunkProgress,
} from '../shared/chunk-progress';
import type { EAN } from '../shared/ean';
import type { NonEmpty } from '../shared/non-empty';
import type { DecodeResult } from './observation';
import {
  type BrandCategoryMeta,
  type BrandSeriesResult,
  type CacheBatchResult,
  type CacheKey,
  type CacheRowResult,
  ObservationRepository,
  type UpdateProbeEntry,
} from './observation-repository';

/** The repository the cache reads through on a miss. */
export const OBSERVATION_SOURCE = new InjectionToken<ObservationRepository>('OBSERVATION_SOURCE');

// portal_cache refreshes daily, so an hour from the read adds little staleness.
const MAX_AGE_MS = 60 * 60 * 1000;

// About 80 MB of heap at 160 bytes an observation, measured on `depend` (577 000) 2026-10-04.
const MAX_HELD_OBSERVATIONS = 500_000;

// An entry used inside this window belongs to a scope in use and is never evicted.
const IN_USE_MS = 5 * 60 * 1000;

// Segment of the undated results of a whole-history read with no dated result to sit beside.
const UNDATED = '';

const DAY = /^\d{4}-\d{2}-\d{2}$/;

// One contiguous `YYYY-MM` range, or the whole history.
type Coverage = { readonly first: string; readonly last: string } | 'all';

// A key over its coverage, sliced by the month of each result's day; `found` is false while no
// read returned a row for the key.
type Held = {
  readonly segments: ReadonlyMap<string, readonly DecodeResult[]>;
  readonly upd: string | null;
  readonly hasPayload: boolean;
  readonly found: boolean;
  readonly coverage: Coverage;
};

type Settled = Held & {
  readonly readAt: number;
  readonly usedAt: number;
  readonly weight: number;
};

type SourceRead = {
  readonly keys: readonly CacheKey[];
  // Undefined reads the whole history.
  readonly months: NonEmpty<string> | undefined;
  // Every key of the read, a key with no row held as not found.
  readonly result: Promise<ReadonlyMap<CacheKey, Held>>;
  readonly controller: AbortController;
  waiters: number;
  done: boolean;
};

// The settled part keeps serving its months while a read of more months is in flight.
type Entry = { readonly settled?: Settled; readonly inFlight?: SourceRead };

// A key a pass answers once its reads settle: `base` merged with what `read` returns.
type Pending = { readonly base: Held | undefined; readonly read?: SourceRead };

/**
 * The observation port with per-key rows held for an hour, concurrent reads of a key shared, and
 * the whole cache dropped when the session user changes. The other reads pass straight through.
 *
 * A key holds one contiguous month range or the whole history. A windowed read is served when
 * that range covers it; otherwise the source reads the missing months plus the one held month
 * adjacent to the gap, because the decoder carries Apohja context forward across days and that
 * month was decoded without the context the gap supplies. A request with a gap on both sides
 * re-reads its whole range. A read month replaces a held one only when the read also holds the
 * month before it, so the new decode had its context: a backward extension replaces the adjacent
 * held month, a forward extension reads it for context only and keeps the held copy. A result whose day is not a date or lies outside the months read is
 * held under the first month read, or under the earliest month present on a whole-history read.
 */
@Injectable()
export class CachingObservationRepository extends ObservationRepository {
  private readonly source = inject(OBSERVATION_SOURCE);

  // Insertion order is recency order: an access deletes and re-sets the key.
  private readonly entries = new Map<CacheKey, Entry>();
  private held = 0;
  // Undefined until the first auth event, which only records the user.
  private lastUserId: string | null | undefined;

  constructor() {
    super();
    const { data } = inject(SupabaseService).client.auth.onAuthStateChange((_event, session) => {
      const userId = session?.user.id ?? null;
      if (this.lastUserId !== undefined && userId !== this.lastUserId) this.clear();
      this.lastUserId = userId;
    });
    inject(DestroyRef).onDestroy(() => data.subscription.unsubscribe());
  }

  /**
   * Serves a key from the months it holds, joins a read in flight that will hold the asked
   * months, waits for one that will not and then reads what is still missing, and reads the rest
   * from the source, one read per exact list of missing months.
   *
   * A served row carries the asked months' observations in ascending month order, with the `upd`
   * and `hasPayload` of every month held. A held key whose asked months hold no data is returned
   * with zero observations and its key-wide `upd`, where a direct adapter read would omit it.
   */
  async readBatch(
    keys: readonly CacheKey[],
    monthsYM?: NonEmpty<string>,
    onProgress?: ChunkProgressReporter,
    signal?: AbortSignal
  ): Promise<CacheBatchResult> {
    signal?.throwIfAborted();
    const list = Array.from(new Set(keys.map(String).filter(Boolean))) as CacheKey[];
    if (!list.length) return new Map();

    const answered = new Map<CacheKey, Held>();
    let waited = false;
    let ownReads = 0;
    let todo: readonly CacheKey[] = list;
    while (todo.length) {
      const now = Date.now();
      const pending = new Map<CacheKey, Pending>();
      const later: CacheKey[] = [];
      const reads = new Set<SourceRead>();
      const groups = new Map<string, { months: NonEmpty<string> | undefined; keys: CacheKey[] }>();
      for (const key of todo) {
        const entry = this.entries.get(key);
        const settled = freshOf(entry, now);
        if (settled && covers(settled.coverage, monthsYM)) {
          this.put(key, { ...entry, settled: { ...settled, usedAt: now } });
          answered.set(key, settled);
        } else if (entry?.inFlight) {
          const reading = coverageOf(entry.inFlight.months);
          const ahead = settled ? union(settled.coverage, reading) : reading;
          if (covers(ahead, monthsYM)) pending.set(key, { base: settled, read: entry.inFlight });
          else later.push(key);
          reads.add(entry.inFlight);
          waited = true;
        } else {
          const months = missingMonths(settled?.coverage, monthsYM);
          const id = months?.join(',') ?? '*';
          const group = groups.get(id) ?? { months, keys: [] };
          group.keys.push(key);
          groups.set(id, group);
          pending.set(key, { base: settled });
        }
      }

      const answeredBefore = answered.size;
      const loaded = new Map<string, number>();
      for (const [id, group] of groups) {
        const progress =
          onProgress &&
          ((p: ChunkProgress) => {
            if (signal?.aborted) return;
            loaded.set(id, p.loaded);
            let sum = answeredBefore;
            for (const n of loaded.values()) sum += n;
            reportChunkProgress(onProgress, sum, list.length);
          });
        const read = this.startRead([...group.keys].sort(), group.months, now, progress);
        for (const key of group.keys) pending.set(key, { base: pending.get(key)?.base, read });
        reads.add(read);
        ownReads++;
      }

      const awaited = [...reads];
      const results = new Map(
        (await this.waitFor(awaited, signal)).map((rows, i) => [awaited[i], rows] as const)
      );
      for (const [key, { base, read }] of pending) {
        const next = read && results.get(read)?.get(key);
        const merged = next ? merge(base, next) : base;
        if (merged) answered.set(key, merged);
      }
      todo = later;
    }
    if (waited || !ownReads) reportChunkProgress(onProgress, list.length, list.length);

    const out = new Map<CacheKey, CacheRowResult>();
    for (const key of list) {
      const held = answered.get(key);
      if (held?.found) out.set(key, rowOf(held, monthsYM));
    }
    return out;
  }

  /**
   * Passes the probe through and drops a held key when the newest probed `upd` of its rows inside
   * the months it holds, or of its marker row, differs from the `upd` held; a rewrite outside the
   * months held cannot change what the key serves.
   */
  async probeUpdates(
    eans: readonly EAN[],
    signal?: AbortSignal
  ): Promise<readonly UpdateProbeEntry[]> {
    const probed = await this.source.probeUpdates(eans, signal);
    const byKey = new Map<string, UpdateProbeEntry[]>();
    for (const entry of probed) {
      if (entry.upd === null) continue;
      byKey.set(entry.cKey, [...(byKey.get(entry.cKey) ?? []), entry]);
    }
    for (const ean of eans) {
      const entry = this.entries.get(ean);
      const coverage = entry && heldCoverage(entry);
      if (!entry || !coverage) continue;
      let newest: string | undefined;
      for (const { monthYM, upd } of byKey.get(ean) ?? []) {
        if (upd === null || (monthYM !== 'NA' && !within(coverage, monthYM))) continue;
        if (newest === undefined || Date.parse(upd) > Date.parse(newest)) newest = upd;
      }
      if (newest === undefined) continue;
      if (entry.settled && sameInstant(entry.settled.upd, newest)) continue;
      this.remove(ean);
    }
    return probed;
  }

  invalidate(keys: readonly CacheKey[]): void {
    for (const key of keys) this.remove(key);
    this.source.invalidate(keys);
  }

  readByKey(key: CacheKey, monthsYM?: readonly string[]): Promise<CacheRowResult | null> {
    return this.source.readByKey(key, monthsYM);
  }

  readBrandCategorySeries(
    brandSlugs: readonly string[],
    categoryPrefixes: NonEmpty<string>,
    monthsYM: NonEmpty<string>,
    signal?: AbortSignal
  ): Promise<ReadonlyMap<string, CacheRowResult>> {
    return this.source.readBrandCategorySeries(brandSlugs, categoryPrefixes, monthsYM, signal);
  }

  readWholeBrandSeries(
    brandSlugs: readonly string[],
    monthsYM: NonEmpty<string>,
    signal?: AbortSignal
  ): Promise<ReadonlyMap<string, CacheRowResult>> {
    return this.source.readWholeBrandSeries(brandSlugs, monthsYM, signal);
  }

  loadCategoryMetaOfBrands(
    brandSlugs: readonly string[],
    signal?: AbortSignal
  ): Promise<ReadonlyMap<string, readonly BrandCategoryMeta[]>> {
    return this.source.loadCategoryMetaOfBrands(brandSlugs, signal);
  }

  readBrandSeries(
    pbId: string,
    monthsYM?: readonly string[],
    signal?: AbortSignal
  ): Promise<BrandSeriesResult> {
    return this.source.readBrandSeries(pbId, monthsYM, signal);
  }

  private startRead(
    keys: readonly CacheKey[],
    months: NonEmpty<string> | undefined,
    now: number,
    progress?: ChunkProgressReporter
  ): SourceRead {
    const controller = new AbortController();
    const read: SourceRead = {
      keys,
      months,
      controller,
      waiters: 0,
      done: false,
      result: this.source.readBatch(keys, months, progress, controller.signal).then(
        (rows) => {
          read.done = true;
          const sliced = new Map(keys.map((key) => [key, slice(rows.get(key), months)] as const));
          this.store(read, sliced);
          return sliced;
        },
        (error: unknown) => {
          read.done = true;
          this.dropOwned(read);
          throw error;
        }
      ),
    };
    // A stale settled part goes now: the read replaces it rather than extending it.
    for (const key of keys)
      this.put(key, { settled: freshOf(this.entries.get(key), now), inFlight: read });
    return read;
  }

  private waitFor(
    reads: readonly SourceRead[],
    signal?: AbortSignal
  ): Promise<readonly ReadonlyMap<CacheKey, Held>[]> {
    for (const read of reads) read.waiters++;
    let waiting = true;
    const leave = (): void => {
      if (!waiting) return;
      waiting = false;
      for (const read of reads) this.release(read);
    };
    return new Promise((resolve, reject) => {
      const onAbort = (): void => {
        leave();
        reject(signal?.reason);
      };
      signal?.addEventListener('abort', onAbort, { once: true });
      Promise.all(reads.map((read) => read.result))
        .then(resolve, (error: unknown) => {
          leave();
          reject(error);
        })
        .finally(() => signal?.removeEventListener('abort', onAbort));
    });
  }

  private release(read: SourceRead): void {
    read.waiters--;
    if (read.waiters > 0 || read.done) return;
    this.dropOwned(read);
    read.controller.abort();
  }

  private store(read: SourceRead, sliced: ReadonlyMap<CacheKey, Held>): void {
    const now = Date.now();
    for (const [key, next] of sliced) {
      const entry = this.entries.get(key);
      if (entry?.inFlight !== read) continue;
      const old = entry.settled;
      // The older read sets the age unless this read replaced every month held.
      const readAt = old && !replacesAll(old, next) ? old.readAt : now;
      this.put(key, { settled: settle(merge(old, next), readAt, now) });
    }
    this.evictOver();
  }

  // A failed or abandoned read leaves the months held before it in place.
  private dropOwned(read: SourceRead): void {
    for (const key of read.keys) {
      const entry = this.entries.get(key);
      if (entry?.inFlight !== read) continue;
      if (entry.settled) this.put(key, { settled: entry.settled });
      else this.remove(key);
    }
  }

  private evictOver(): void {
    const now = Date.now();
    for (const [key, entry] of this.entries) {
      if (this.held <= MAX_HELD_OBSERVATIONS) return;
      if (!entry.settled || entry.inFlight) continue;
      // Recency order: every settled entry after this one is in use too.
      if (now - entry.settled.usedAt < IN_USE_MS) return;
      this.remove(key);
    }
  }

  private put(key: CacheKey, entry: Entry): void {
    this.remove(key);
    this.entries.set(key, entry);
    this.held += entry.settled?.weight ?? 0;
  }

  private remove(key: CacheKey): void {
    this.held -= this.entries.get(key)?.settled?.weight ?? 0;
    this.entries.delete(key);
  }

  private clear(): void {
    this.entries.clear();
    this.held = 0;
  }
}

function freshOf(entry: Entry | undefined, now: number): Settled | undefined {
  const settled = entry?.settled;
  return settled && now - settled.readAt < MAX_AGE_MS ? settled : undefined;
}

function coverageOf(months: NonEmpty<string> | undefined): Coverage {
  return months ? { first: months[0], last: months[months.length - 1] } : 'all';
}

function within(coverage: Coverage, month: string): boolean {
  return coverage === 'all' || (coverage.first <= month && month <= coverage.last);
}

function covers(coverage: Coverage, months: NonEmpty<string> | undefined): boolean {
  if (coverage === 'all') return true;
  if (!months) return false;
  return coverage.first <= months[0] && months[months.length - 1] <= coverage.last;
}

function contains(outer: Coverage, inner: Coverage): boolean {
  return inner === 'all' ? outer === 'all' : covers(outer, [inner.first, inner.last]);
}

// Callers keep the two ranges contiguous, so the hull is their union.
function union(a: Coverage, b: Coverage): Coverage {
  if (a === 'all' || b === 'all') return 'all';
  return {
    first: a.first < b.first ? a.first : b.first,
    last: a.last > b.last ? a.last : b.last,
  };
}

function heldCoverage(entry: Entry): Coverage | undefined {
  const reading = entry.inFlight && coverageOf(entry.inFlight.months);
  if (entry.settled && reading) return union(entry.settled.coverage, reading);
  return entry.settled?.coverage ?? reading;
}

// The months to read so the coverage becomes contiguous over the asked ones, re-reading the held
// month adjacent to the gap; gaps on both sides re-read the asked range.
function missingMonths(
  coverage: Coverage | undefined,
  months: NonEmpty<string> | undefined
): NonEmpty<string> | undefined {
  if (!months || !coverage || coverage === 'all') return months;
  const first = months[0];
  const last = months[months.length - 1];
  const backward = first < coverage.first;
  const forward = last > coverage.last;
  if (backward && !forward) return monthsBetween(first, coverage.first);
  if (forward && !backward) return monthsBetween(coverage.last, last);
  return months;
}

function monthsBetween(first: string, last: string): NonEmpty<string> {
  const out: [string, ...string[]] = [first];
  let year = Number(first.slice(0, 4));
  let month = Number(first.slice(5, 7));
  for (let ym = first; ym < last; ) {
    month = month === 12 ? 1 : month + 1;
    if (month === 1) year++;
    ym = `${year}-${String(month).padStart(2, '0')}`;
    out.push(ym);
  }
  return out;
}

function dayOf(result: DecodeResult): string {
  return result.kind === 'ok' ? result.observation.day : result.day;
}

function slice(row: CacheRowResult | undefined, months: NonEmpty<string> | undefined): Held {
  const coverage = coverageOf(months);
  const segments = new Map<string, DecodeResult[]>();
  if (!row) return { segments, upd: null, hasPayload: false, found: false, coverage };
  const undated: DecodeResult[] = [];
  for (const result of row.observations) {
    const day = dayOf(result);
    const month = day.slice(0, 7);
    if (!DAY.test(day) || !within(coverage, month)) undated.push(result);
    else {
      const segment = segments.get(month);
      if (segment) segment.push(result);
      else segments.set(month, [result]);
    }
  }
  if (undated.length) {
    const home = months?.[0] ?? [...segments.keys()].sort()[0] ?? UNDATED;
    segments.set(home, [...(segments.get(home) ?? []), ...undated]);
  }
  return { segments, upd: row.upd, hasPayload: row.hasPayload, found: true, coverage };
}

// A read month was decoded with the Apohja context of the month before it when the read holds
// that month too, which is every month but its first.
function decodedWithContext(coverage: Coverage, month: string): boolean {
  return coverage === 'all' || (month > coverage.first && month <= coverage.last);
}

// The read replaces every held month: it covers them all and holds the month before each.
function replacesAll(base: Held, next: Held): boolean {
  if (next.coverage === 'all') return true;
  return (
    base.coverage !== 'all' &&
    contains(next.coverage, base.coverage) &&
    next.coverage.first < base.coverage.first
  );
}

// A read month replaces a held month when the read decoded it with context; a held month the
// read decoded without context keeps the held copy. No base (an aged-out entry was dropped when
// its re-read started) takes the read whole.
function merge(base: Held | undefined, next: Held): Held {
  if (!base || replacesAll(base, next)) return next;
  const segments = new Map(base.segments);
  const keepsHeld = (month: string): boolean =>
    within(base.coverage, month) && !decodedWithContext(next.coverage, month);
  for (const month of base.segments.keys()) {
    if (within(next.coverage, month) && !keepsHeld(month)) segments.delete(month);
  }
  for (const [month, results] of next.segments) {
    if (!keepsHeld(month)) segments.set(month, results);
  }
  return {
    segments,
    upd: newest(base.upd, next.upd),
    hasPayload: base.hasPayload || next.hasPayload,
    found: base.found || next.found,
    coverage: union(base.coverage, next.coverage),
  };
}

function settle(held: Held, readAt: number, now: number): Settled {
  let weight = 0;
  for (const results of held.segments.values()) weight += results.length;
  return { ...held, readAt, usedAt: now, weight };
}

function rowOf(held: Held, months: NonEmpty<string> | undefined): CacheRowResult {
  const order = months ?? [...held.segments.keys()].sort();
  return {
    upd: held.upd,
    hasPayload: held.hasPayload,
    observations: order.flatMap((month) => held.segments.get(month) ?? []),
  };
}

function newest(a: string | null, b: string | null): string | null {
  if (a === null) return b;
  if (b === null) return a;
  return Date.parse(b) > Date.parse(a) ? b : a;
}

function sameInstant(a: string | null, b: string): boolean {
  if (a === null) return false;
  const left = Date.parse(a);
  return !Number.isNaN(left) && left === Date.parse(b);
}
